import React, { useEffect } from 'react';

import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { CloseIcon } from '../../icons/CloseIcon';
import { Body, CloseButton, Footer, Overlay, Panel } from './Drawer.styles';
import type { DrawerProps } from './Drawer.types';

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  'aria-label': ariaLabel,
  closeLabel,
  closeIcon,
  footer,
  width,
  mobileTopOffset,
  backdropBlur = false,
  placement = 'right',
  desktopOffset,
  closeButtonPlacement = 'default',
}) => {
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const panelStyle = {
    ...(width ? { '--drawer-width': width } : {}),
    ...(desktopOffset ? { '--drawer-desktop-offset': desktopOffset } : {}),
    ...(mobileTopOffset ? { '--drawer-mobile-top': mobileTopOffset } : {}),
  } as React.CSSProperties;

  const overlayStyle = (
    desktopOffset ? { '--drawer-desktop-offset': desktopOffset } : {}
  ) as React.CSSProperties;

  return (
    <>
      <Overlay
        data-open={String(open)}
        data-blur={String(backdropBlur)}
        data-placement={placement}
        onClick={onClose}
        aria-hidden="true"
        style={overlayStyle}
      />
      <Panel
        data-open={String(open)}
        data-placement={placement}
        role="dialog"
        aria-modal={open}
        aria-label={ariaLabel}
        aria-hidden={!open}
        style={panelStyle}
      >
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          data-placement-variant={closeButtonPlacement}
        >
          {closeIcon ?? <CloseIcon size={20} />}
        </CloseButton>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </>
  );
};
